import React, { useState, useContext } from 'react';
import { Input, Option, Select } from '@material-tailwind/react';

import InputCardsContext from '../../../context/InputCardsContext';
import { checkForInvalidNameInput } from '../../../functions/InputChecks';
import { LocationService, Location, RestaurantService } from '../../../interfaces';

const LocationInputCard = (props: {
  arrLocationService: Array<LocationService>;
  arrNewLocation: Array<Location>;
  companyId: number;
  locationId: number;
  locationService: LocationService | undefined;
  restaurantId: number;
  updateEditedOrNewLocation: any;
  arrRestaurantService: Array<RestaurantService>;
}) => {
  const inputCardsContext = useContext(InputCardsContext);

  const isEdit = !!props.locationService;
  // If LocationInputCard is for editing existing location, isNameValid should be true
  const [isNameValid, setIsNameValid] = useState(isEdit);
  const [nameErrorMessage, setNameErrorMessage] = useState(isEdit ? '' : 'Field cannot be empty!');
  // TODO: To change naming for companyForInput, restaurantForInput, locationForInput and serviceForInput as variable naming is
  // not descriptive enough to convey what the variable is (Ref: #70)
  const [locationForInput, setLocationForInput] = useState<Location & { restaurantName?: string }>(
    isEdit && props.locationService !== undefined
      ? {
          locationId: props.locationService.locationId,
          name: '',
          restaurantId: props.locationService.restaurantId,
          restaurantName: '',
        }
      : {
          locationId: props.locationId,
          name: '',
          restaurantId: props.restaurantId,
        }
  );
  const [isRestaurantNameValid, setIsRestaurantNameValid] = useState(false);
  const [restaurantNameErrorMessage, setRestaurantNameErrorMessage] =
    useState('Field cannot be empty!');

  /**
   * Function attached to input component, takes in input data for editing this locations's name and updates newLocationService state.
   * @param event - contains input data from input html
   */
  const updateLocationNameOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLocationForInput = {
      ...locationForInput,
      name: event.target.value,
    };
    setLocationForInput(newLocationForInput);
  };

  /**
   * Function runs whenever onBlur event on location name Input component occurs. Validate the location name
   * - Check for alphanumeric
   * - Cannot duplicate with any new or existing location name within a restaurant
   * Note: If it is editing an existing location, empty string ('') is considered a valid input as it will be treated as no change to the
   * current name. Hence there is slight difference in the handling of input between edit and add.
   */
  const locationNameInputOnBlur = () => {
    if (isEdit) {
      if (
        locationForInput.name.length === 0 ||
        locationForInput.name === props.locationService!.name
      ) {
        // This setting of locationForInput is because the user might type in the same exact location name as the original,
        // and therefore the serviceForInput's name should be reset to '' to indicate that there is no change.
        const newLocationForInput = { ...locationForInput, name: '' };
        setIsNameValid(true);
        setLocationForInput(newLocationForInput);

        if (locationForInput.restaurantId === props.locationService?.restaurantId) {
          inputCardsContext.deleteErrorForOneId(locationForInput.locationId, 1);
          props.updateEditedOrNewLocation({ locationId: locationForInput.locationId });
        }
      } else {
        const errorMessage = checkForInvalidNameInput(
          locationForInput.name,
          locationForInput.locationId,
          props.arrLocationService,
          [],
          'Location'
        );
        if (errorMessage.length !== 0) {
          setIsNameValid(false);
          setNameErrorMessage(errorMessage);
          inputCardsContext.setErrorExist(locationForInput.locationId, true, 1);
        } else {
          setIsNameValid(true);
          if (
            locationForInput.restaurantId !== -1 ||
            (locationForInput.restaurantId === -1 && isRestaurantNameValid)
          ) {
            inputCardsContext.deleteErrorForOneId(locationForInput.locationId, 1);
          }
        }
        props.updateEditedOrNewLocation(
          { locationId: locationForInput.locationId },
          locationForInput
        );
      }
    } else {
      props.updateEditedOrNewLocation(
        { locationId: props.locationId, restaurantId: props.restaurantId },
        locationForInput
      );
      if (locationForInput.name.length === 0) {
        setIsNameValid(false);
        setNameErrorMessage('Field cannot be empty!');
        inputCardsContext.setErrorExist(props.locationId, true, 1);
      } else {
        const errorMessage = checkForInvalidNameInput(
          locationForInput.name,
          props.locationId,
          props.arrLocationService,
          props.arrNewLocation,
          'Location'
        );
        if (errorMessage.length !== 0) {
          setIsNameValid(false);
          setNameErrorMessage(errorMessage);
          inputCardsContext.setErrorExist(props.locationId, true, 1);
        } else {
          setIsNameValid(true);
          inputCardsContext.deleteErrorForOneId(props.locationId, 1);
        }
      }
    }
  };

  /**
   * Function attached to input component, takes in input data for editing this locations's restaurant id and updates newLocationService state.
   * Value is typed as any due to conflict with default onChange type and actual value passed in, which is a string
   * @param value - contains input data from Select component
   */
  const updateRestaurantIdOnChange = (value: any) => {
    const restaurantIdToUpdateTo = Number(value);
    const newLocationForInput = {
      ...locationForInput,
      restaurantId: restaurantIdToUpdateTo,
    };

    if (
      locationForInput.name.length === 0 &&
      newLocationForInput.restaurantId === props.locationService!.restaurantId
    ) {
      inputCardsContext.deleteErrorForOneId(locationForInput.locationId, 1);
      props.updateEditedOrNewLocation({ locationId: locationForInput.locationId });
    } else {
      props.updateEditedOrNewLocation(
        { locationId: locationForInput.locationId },
        newLocationForInput
      );
      if (restaurantIdToUpdateTo === -1) {
        if (!isRestaurantNameValid) {
          inputCardsContext.setErrorExist(props.locationId, true, 1);
        } else if (isNameValid && isRestaurantNameValid) {
          inputCardsContext.deleteErrorForOneId(locationForInput.locationId, 1);
        }
      } else if (isNameValid) {
        inputCardsContext.deleteErrorForOneId(locationForInput.locationId, 1);
      }
    }
    setLocationForInput(newLocationForInput);
  };

  /**
   * Function attached to input component, takes in input data for editing this restaurant's name and updates locationForInput state.
   * @param event - contains input data from html input element
   */
  const updateRestaurantNameOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newLocationForInput = {
      ...locationForInput,
      restaurantName: event.target.value,
    };
    setLocationForInput(newLocationForInput);
  };

  /**
   * Function runs whenever onBlur event on restaurant name Input component occurs. Validate the restaurant name
   * - Check for alphanumeric
   * - Cannot duplicate with any new or existing restaurant name within a company
   * Note: This function is only called on edit of location and will not be used for location creation
   */
  const restaurantNameInputOnBlur = () => {
    if (locationForInput.restaurantName!.length === 0) {
      setIsRestaurantNameValid(false);
      setRestaurantNameErrorMessage('Field cannot be empty!');
      inputCardsContext.setErrorExist(props.locationId, true, 1);
    } else {
      const errorMessage = checkForInvalidNameInput(
        locationForInput.restaurantName!,
        -1,
        props.arrRestaurantService,
        [],
        'Property'
      );
      if (errorMessage.length !== 0) {
        setIsRestaurantNameValid(false);
        setRestaurantNameErrorMessage(errorMessage);
        inputCardsContext.setErrorExist(props.locationId, true, 1);
      } else {
        setIsRestaurantNameValid(true);
        if (isNameValid) {
          inputCardsContext.deleteErrorForOneId(locationForInput.locationId, 1);
        }
      }
      props.updateEditedOrNewLocation(
        { locationId: locationForInput.locationId },
        locationForInput
      );
    }
  };

  return (
    <div className="flex justify-between py-4">
      <div className="flex flex-col gap-x-1 mt-2 px-4">
        <Input
          onBlur={locationNameInputOnBlur}
          size="md"
          onChange={updateLocationNameOnChange}
          label="Location"
          variant="static"
          value={locationForInput.name}
          placeholder={props.locationService !== undefined ? props.locationService.name : ''}
          className={`LocationNameInput text-off-black`}
          error={!isNameValid}
        />
        {!isNameValid && (
          <p className="LocationNameErrorMessage text-red-500 text-xs mt-2">{nameErrorMessage}</p>
        )}
      </div>
      {isEdit ? (
        <div className="flex flex-col shrink gap-5 mr-3 w-60">
          <Select
            disabled={props.arrLocationService.length === 1}
            label="Property"
            variant="static"
            value={`${locationForInput.restaurantId}`}
            className={`${
              locationForInput.restaurantId === props.restaurantId ? 'text-[#a0a0a0]' : 'text-black'
            }`}
            onChange={updateRestaurantIdOnChange}
          >
            {[...props.arrRestaurantService, { restaurantId: -1, name: '(New Property)' }].map(
              (restaurantService, index) => {
                return (
                  <Option
                    value={`${restaurantService.restaurantId}`}
                    key={`RestaurantOption_${index}`}
                  >
                    {restaurantService.name}
                  </Option>
                );
              }
            )}
          </Select>

          {locationForInput.restaurantId === -1 ? (
            <div className="flex flex-col gap-x-1 mt-2">
              <Input
                variant="static"
                label="New Property Name"
                size="md"
                value={locationForInput.restaurantName}
                onChange={updateRestaurantNameOnChange}
                onBlur={restaurantNameInputOnBlur}
              />
              {!isRestaurantNameValid && (
                <p className="PropertyNameErrorMessage text-red-500 text-xs mt-2">
                  {restaurantNameErrorMessage}
                </p>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default LocationInputCard;
